import "./style.scss";
import { Dispatch, FC, SetStateAction, useEffect, useReducer, useState } from "react";
import { IApplyInfoData, IRowData } from "@/src/types/applicationOnline";
import { ITasksTableRow } from "@/src/types/tasksTable";
import { IOfficeAccountingTableRow } from "@/src/types/officeAccounting";
import VerticalLine from "@/src/components/VerticalLine/VerticalLine";
import { useFormatDateDisplay } from "@/src/hooks/useFormatDateDisplay";
import { getEmailValidationError, getPhoneValidationError } from "@/src/utils/contactValidation";
import { resolvePopUpDegree } from "@/src/utils/applications/resolvePopUpDegree";
import { resolveDisplayDegreeAndCourse } from "@/src/utils/admissionOffice/resolveDisplayDegreeAndCourse";
import CustomSingleSelect from "@/src/components/CustomUtils/CustomSingleSelect/CustomSingleSelect";
import CustomDate from "@/src/components/CustomUtils/CustomDate/CustomDate";
import SelectorArrowSvg from "@/src/svg/applicationsPage/APPopUp/CustomDocumentPreparation/SelectorArrowSvg";
import DateSvg from "@/src/svg/applicationsPage/APPopUp/CustomDocumentPreparation/DateSvg";
import { Countries, Nationalities, pushUniqueOption } from "@/src/constants/globalConsts";
import {
  MALTA_FORM_OF_EDUCATION_LABELS,
  normalizeFormOfEducationValue,
} from "@/src/constants/AdmissionOffice/Malta/maltaFormOfEducationOptions";
import {
  resolveStudentStatusId,
  resolveStudentStatusLabel,
  studentStatusConst,
  studentStatusLabels,
} from "@/src/constants/studentStatusConsts";

type IUserInfo = IRowData["userInfo"];

interface IGeneralInformation {
  viewData: IUserInfo | undefined;
  draftData: IUserInfo | undefined;
  currentApplyInfo?: PopUpContextRow;
  editor: boolean;
  showValidationErrors?: boolean;
  setEditorDraft: Dispatch<SetStateAction<IUserInfo | undefined>>;
}

type PopUpContextRow = IApplyInfoData | ITasksTableRow | IOfficeAccountingTableRow;

const isTasksTableRow = (row?: PopUpContextRow): row is ITasksTableRow => row != null && "task_id" in row;

const isOfficeAccountingTableRow = (row?: PopUpContextRow): row is IOfficeAccountingTableRow =>
  row != null && "student_info_id" in row;

const isApplicationRow = (row?: PopUpContextRow): row is IApplyInfoData =>
  row != null && !isTasksTableRow(row) && !isOfficeAccountingTableRow(row);

const displayText = (value?: string | null): string => (value?.trim() ? value : "—");

const formatAccountingDisplay = (accounting: unknown): string => {
  if (accounting == null) return "—";
  if (typeof accounting !== "object") return String(accounting);
  const record = accounting as Record<string, unknown>;
  const generalStatus = record.general_status ?? record.generalStatus;
  if (typeof generalStatus === "string" && generalStatus.trim()) return generalStatus;
  const admissionNo = record.admission_no ?? record.admissionNo;
  if (admissionNo != null && String(admissionNo).trim()) return String(admissionNo);
  return "—";
};

const GeneralInformation: FC<IGeneralInformation> = ({
  viewData,
  draftData,
  currentApplyInfo,
  editor,
  showValidationErrors = false,
  setEditorDraft,
}) => {
  const formatDateDisplay = useFormatDateDisplay();
  const taskRow = isTasksTableRow(currentApplyInfo) ? currentApplyInfo : undefined;
  const officeAccountingRow = isOfficeAccountingTableRow(currentApplyInfo) ? currentApplyInfo : undefined;
  const applicationRow = isApplicationRow(currentApplyInfo) ? currentApplyInfo : undefined;

  const [openSelector, setOpenSelector] = useState<string | null>(null);
  const [, refreshSelectLists] = useReducer((n: number) => n + 1, 0);

  const emailError = editor ? getEmailValidationError(draftData?.email) : undefined;
  const phoneError = editor ? getPhoneValidationError(draftData?.phone) : undefined;

  useEffect(() => {
    if (!editor) setOpenSelector(null);
  }, [editor]);

  useEffect(() => {
    if (!editor) return;
    pushUniqueOption(Countries, draftData?.country ?? "");
    pushUniqueOption(Nationalities, draftData?.nationality ?? "");
    refreshSelectLists();
  }, [editor, draftData?.country, draftData?.nationality]);

  const updateUserInfo = (key: keyof IUserInfo, value: string) => {
    setEditorDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleAddCountryOption = (value: string) => {
    pushUniqueOption(Countries, value);
    refreshSelectLists();
    updateUserInfo("country", value);
  };

  const handleAddNationalityOption = (value: string) => {
    pushUniqueOption(Nationalities, value);
    refreshSelectLists();
    updateUserInfo("nationality", value);
  };

  const degreeValue = resolvePopUpDegree(currentApplyInfo) || resolvePopUpDegree(viewData);
  const courseValue =
    (officeAccountingRow?.course && officeAccountingRow.course.trim()) ||
    resolveDisplayDegreeAndCourse(viewData?.course, degreeValue).course;
  const formStudyValue = normalizeFormOfEducationValue(applicationRow?.formStudy ?? viewData?.formStudy);
  const canEditFormStudy = Boolean(applicationRow);
  const studentStatusViewLabel = resolveStudentStatusLabel(viewData?.studentStatus);
  const studentStatusViewId = resolveStudentStatusId(viewData?.studentStatus);
  const studentStatusDraftLabel = resolveStudentStatusLabel(draftData?.studentStatus);
  const studentStatusDraftId = resolveStudentStatusId(draftData?.studentStatus);
  const studentStatusDraftValue = studentStatusDraftLabel === "—" ? "" : studentStatusDraftLabel;

  return (
    <div className="general-information">
      <div className="general-information-title">
        <p>{viewData?.fullname ?? "full name"}</p>
      </div>
      <div className="general-information-main">
        <div className="general-information-main-block">
          <div className="general-information-main-block-item">
            <p>Data</p>
            <VerticalLine />
            <span>
              {taskRow?.created_date
                ? formatDateDisplay(taskRow.created_date)
                : applicationRow
                  ? formatDateDisplay(applicationRow.date)
                  : "—"}
            </span>
          </div>
          <div className="general-information-main-block-item">
            <p>Updated</p>
            <VerticalLine />
            <span>
              {taskRow?.priority
                ? formatDateDisplay(taskRow.priority)
                : applicationRow
                  ? formatDateDisplay(applicationRow.updated)
                  : "—"}
            </span>
          </div>
          <div className="general-information-main-block-item">
            <p>{taskRow ? "task_id" : officeAccountingRow ? "Student Info" : "Application"}</p>
            <VerticalLine />
            <span>{taskRow?.task_id ?? applicationRow?.id ?? officeAccountingRow?.student_info_id ?? "—"}</span>
          </div>
        </div>
        <div className="general-information-main-block">
          <div className="general-information-main-block-item">
            <p>ID No</p>
            <VerticalLine />
            <span>{applicationRow?.applicant_id ?? taskRow?.student_id ?? officeAccountingRow?.applicant_id ?? "—"}</span>
          </div>
          <div className="general-information-main-block-item">
            <p>Status</p>
            <VerticalLine />
            <span>{viewData?.role ? viewData?.role : "-"}</span>
          </div>
          <div className="general-information-main-block-item">
            <p>Payment Status</p>
            <VerticalLine />
            <div className={`status-option not_sorted`}>
              <span>Not Sorted</span>
            </div>
          </div>
        </div>
      </div>
      <div className="general-information-full">
        <div className="general-information-full-item">
          <div className="general-information-full-item-start">
            <p>Full name (EN)</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-middle ${editor ? "editor" : ""}`}>
            <p>{viewData?.fullname}</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-end ${editor ? "editor" : ""}`}>
            {editor ? (
              <input
                placeholder="New meaning"
                value={draftData?.fullname ?? ""}
                onChange={(e) => updateUserInfo("fullname", e.target.value)}
              />
            ) : (
              <p>-</p>
            )}
          </div>
        </div>
        <div className="general-information-full-item">
          <div className="general-information-full-item-start">
            <p>Full name (Nat.)</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-middle ${editor ? "editor" : ""}`}>
            <p>{viewData?.fullnameNat}</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-end ${editor ? "editor" : ""}`}>
            {editor ? (
              <input
                placeholder="New meaning"
                value={draftData?.fullnameNat ?? ""}
                onChange={(e) => updateUserInfo("fullnameNat", e.target.value)}
              />
            ) : (
              <p>-</p>
            )}
          </div>
        </div>
        <div className="general-information-full-item">
          <div className="general-information-full-item-start">
            <p>Email</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-middle ${editor ? "editor" : ""}`}>
            <p>{viewData?.email}</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-end ${editor ? "editor" : ""}`}>
            {editor ? (
              <>
                <input
                  type="email"
                  placeholder="New meaning"
                  className={emailError && showValidationErrors ? "invalid" : ""}
                  value={draftData?.email ?? ""}
                  onChange={(e) => updateUserInfo("email", e.target.value)}
                />
              </>
            ) : (
              <p>-</p>
            )}
          </div>
        </div>
        <div className="general-information-full-item">
          <div className="general-information-full-item-start">
            <p>Phone</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-middle ${editor ? "editor" : ""}`}>
            <p>{viewData?.phone}</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-end ${editor ? "editor" : ""}`}>
            {editor ? (
              <>
                <input
                  type="tel"
                  placeholder="New meaning"
                  className={phoneError && showValidationErrors ? "invalid" : ""}
                  value={draftData?.phone ?? ""}
                  onChange={(e) => updateUserInfo("phone", e.target.value)}
                />
              </>
            ) : (
              <p>-</p>
            )}
          </div>
        </div>
        <div className="general-information-full-item">
          <div className="general-information-full-item-start">
            <p>Student Status</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-middle ${editor ? "editor" : ""}`}>
            <div className={`status-option ${editor ? studentStatusDraftId : studentStatusViewId}`}>
              <span className="status-label">{editor ? studentStatusDraftLabel : studentStatusViewLabel}</span>
            </div>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-end ${editor ? "editor" : ""}`}>
            {editor ? (
              <div className="general-information-full-item-select">
                <input
                  readOnly
                  placeholder="Select student status"
                  value={studentStatusDraftValue}
                  onClick={() => setOpenSelector((prev) => (prev === "studentStatus" ? null : "studentStatus"))}
                />
                <SelectorArrowSvg />
                {openSelector === "studentStatus" && (
                  <CustomSingleSelect
                    title="Student Status"
                    data={[...studentStatusLabels]}
                    isSearch={false}
                    allowAdd={false}
                    columnValue={studentStatusDraftValue}
                    onChange={(value) => {
                      const matched = studentStatusConst.find((item) => item.label === value);
                      updateUserInfo("studentStatus", matched?.id ?? value);
                    }}
                    onClose={() => setOpenSelector(null)}
                  />
                )}
              </div>
            ) : (
              <p>-</p>
            )}
          </div>
        </div>
        <div className="general-information-full-item">
          <div className="general-information-full-item-start">
            <p>Address</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-middle ${editor ? "editor" : ""}`}>
            <p>{viewData?.address}</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-end ${editor ? "editor" : ""}`}>
            {editor ? (
              <input
                placeholder="New meaning"
                value={draftData?.address ?? ""}
                onChange={(e) => updateUserInfo("address", e.target.value)}
              />
            ) : (
              <p>-</p>
            )}
          </div>
        </div>
        <div className="general-information-full-item">
          <div className="general-information-full-item-start">
            <p>Degree</p>
          </div>
          <VerticalLine />
          <div className="general-information-full-item-middle">
            <p>{displayText(degreeValue)}</p>
          </div>
          <VerticalLine />
          <div className="general-information-full-item-end">
            <p>—</p>
          </div>
        </div>
        <div className="general-information-full-item">
          <div className="general-information-full-item-start">
            <p>Course</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-middle ${editor ? "editor" : ""}`}>
            <p>{displayText(courseValue)}</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-end ${editor ? "editor" : ""}`}>
            {editor ? (
              <input placeholder="New meaning" value={draftData?.course ?? ""} onChange={(e) => updateUserInfo("course", e.target.value)} />
            ) : (
              <p>-</p>
            )}
          </div>
        </div>
        <div className="general-information-full-item">
          <div className="general-information-full-item-start">
            <p>Form of Education</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-middle ${editor && canEditFormStudy ? "editor" : ""}`}>
            <p>{displayText(formStudyValue)}</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-end ${editor && canEditFormStudy ? "editor" : ""}`}>
            {editor && canEditFormStudy ? (
              <div className="general-information-full-item-select">
                <input
                  readOnly
                  placeholder="Select form of education"
                  value={draftData?.formStudy ?? ""}
                  onClick={() => setOpenSelector((prev) => (prev === "formStudy" ? null : "formStudy"))}
                />
                <SelectorArrowSvg />
                {openSelector === "formStudy" && (
                  <CustomSingleSelect
                    title="Form of Education"
                    data={MALTA_FORM_OF_EDUCATION_LABELS}
                    isSearch={false}
                    allowAdd={false}
                    columnValue={draftData?.formStudy ?? ""}
                    onChange={(value) => updateUserInfo("formStudy", normalizeFormOfEducationValue(value))}
                    onClose={() => setOpenSelector(null)}
                  />
                )}
              </div>
            ) : (
              <p>—</p>
            )}
          </div>
        </div>
        <div className="general-information-full-item">
          <div className="general-information-full-item-start">
            <p>Language</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-middle ${editor ? "editor" : ""}`}>
            <p>{viewData?.language}</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-end ${editor ? "editor" : ""}`}>
            {editor ? (
              <input
                placeholder="New meaning"
                value={draftData?.language ?? ""}
                onChange={(e) => updateUserInfo("language", e.target.value)}
              />
            ) : (
              <p>-</p>
            )}
          </div>
        </div>
        <div className="general-information-full-item">
          <div className="general-information-full-item-start">
            <p>Country</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-middle ${editor ? "editor" : ""}`}>
            <p>{viewData?.country}</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-end ${editor ? "editor" : ""}`}>
            {editor ? (
              <div className="general-information-full-item-select">
                <input
                  readOnly
                  placeholder="Select country"
                  value={draftData?.country ?? ""}
                  onClick={() => setOpenSelector((prev) => (prev === "country" ? null : "country"))}
                />
                <SelectorArrowSvg />
                {openSelector === "country" && (
                  <CustomSingleSelect
                    title="Country"
                    data={Countries}
                    isSearch={true}
                    allowAdd={true}
                    columnValue={draftData?.country ?? ""}
                    onChange={(value) => updateUserInfo("country", value)}
                    onAddItem={handleAddCountryOption}
                    onClose={() => setOpenSelector(null)}
                  />
                )}
              </div>
            ) : (
              <p>-</p>
            )}
          </div>
        </div>
        <div className="general-information-full-item">
          <div className="general-information-full-item-start">
            <p>Nationality</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-middle ${editor ? "editor" : ""}`}>
            <p>{viewData?.nationality}</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-end ${editor ? "editor" : ""}`}>
            {editor ? (
              <div className="general-information-full-item-select">
                <input
                  readOnly
                  placeholder="Select nationality"
                  value={draftData?.nationality ?? ""}
                  onClick={() => setOpenSelector((prev) => (prev === "nationality" ? null : "nationality"))}
                />
                <SelectorArrowSvg />
                {openSelector === "nationality" && (
                  <CustomSingleSelect
                    title="Nationality"
                    data={Nationalities}
                    isSearch={true}
                    allowAdd={true}
                    columnValue={draftData?.nationality ?? ""}
                    onChange={(value) => updateUserInfo("nationality", value)}
                    onAddItem={handleAddNationalityOption}
                    onClose={() => setOpenSelector(null)}
                  />
                )}
              </div>
            ) : (
              <p>-</p>
            )}
          </div>
        </div>
        <div className="general-information-full-item">
          <div className="general-information-full-item-start">
            <p>Date of Birth</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-middle ${editor ? "editor" : ""}`}>
            <p>{viewData?.dateOfBirth ? formatDateDisplay(viewData.dateOfBirth, { dateOnly: true }) : "-"}</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-end ${editor ? "editor" : ""}`}>
            {editor ? (
              <div className="general-information-full-item-date">
                <input
                  readOnly
                  placeholder="Select date"
                  value={draftData?.dateOfBirth ? formatDateDisplay(draftData.dateOfBirth, { dateOnly: true }) : ""}
                  onClick={() => setOpenSelector((prev) => (prev === "dateOfBirth" ? null : "dateOfBirth"))}
                />
                <DateSvg />
                {openSelector === "dateOfBirth" && (
                  <CustomDate
                    value={draftData?.dateOfBirth ?? ""}
                    onChange={(value) => updateUserInfo("dateOfBirth", value)}
                    onClose={() => setOpenSelector(null)}
                  />
                )}
              </div>
            ) : (
              <p>-</p>
            )}
          </div>
        </div>
        <div className="general-information-full-item">
          <div className="general-information-full-item-start">
            <p>Residence</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-middle ${editor ? "editor" : ""}`}>
            <p>{viewData?.residence}</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-end ${editor ? "editor" : ""}`}>
            {editor ? (
              <input
                placeholder="New meaning"
                value={draftData?.residence ?? ""}
                onChange={(e) => updateUserInfo("residence", e.target.value)}
              />
            ) : (
              <p>-</p>
            )}
          </div>
        </div>
        <div className="general-information-full-item">
          <div className="general-information-full-item-start">
            <p>Contact person</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-middle ${editor ? "editor" : ""}`}>
            <p>{viewData?.contactPerson}</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-end ${editor ? "editor" : ""}`}>
            {editor ? (
              <input
                placeholder="New meaning"
                value={draftData?.contactPerson ?? ""}
                onChange={(e) => updateUserInfo("contactPerson", e.target.value)}
              />
            ) : (
              <p>-</p>
            )}
          </div>
        </div>
        <div className="general-information-full-item">
          <div className="general-information-full-item-start">
            <p>Office</p>
          </div>
          <VerticalLine />
          <div className="general-information-full-item-middle">
            <p>{displayText(viewData?.form_office)}</p>
          </div>
          <VerticalLine />
          <div className="general-information-full-item-end">
            <p>—</p>
          </div>
        </div>
        <div className="general-information-full-item">
          <div className="general-information-full-item-start">
            <p>Passport Number</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-middle ${editor ? "editor" : ""}`}>
            <p>{displayText(viewData?.passportNo)}</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-end ${editor ? "editor" : ""}`}>
            {editor ? (
              <input
                placeholder="New meaning"
                value={draftData?.passportNo ?? ""}
                onChange={(e) => updateUserInfo("passportNo", e.target.value)}
              />
            ) : (
              <p>-</p>
            )}
          </div>
        </div>
        <div className="general-information-full-item">
          <div className="general-information-full-item-start">
            <p>Passport Issued</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-middle ${editor ? "editor" : ""}`}>
            <p>{displayText(viewData?.passportIssued)}</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-end ${editor ? "editor" : ""}`}>
            {editor ? (
              <input
                placeholder="New meaning"
                value={draftData?.passportIssued ?? ""}
                onChange={(e) => updateUserInfo("passportIssued", e.target.value)}
              />
            ) : (
              <p>-</p>
            )}
          </div>
        </div>
        <div className="general-information-full-item">
          <div className="general-information-full-item-start">
            <p>Valid Passport</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-middle ${editor ? "editor" : ""}`}>
            <p>{viewData?.validPassport ? formatDateDisplay(viewData.validPassport, { dateOnly: true }) : "—"}</p>
          </div>
          <VerticalLine />
          <div className={`general-information-full-item-end ${editor ? "editor" : ""}`}>
            {editor ? (
              <div className="general-information-full-item-date">
                <input
                  readOnly
                  placeholder="Select date"
                  value={draftData?.validPassport ? formatDateDisplay(draftData.validPassport, { dateOnly: true }) : ""}
                  onClick={() => setOpenSelector((prev) => (prev === "validPassport" ? null : "validPassport"))}
                />
                <DateSvg />
                {openSelector === "validPassport" && (
                  <CustomDate
                    value={draftData?.validPassport ?? ""}
                    onChange={(value) => updateUserInfo("validPassport", value)}
                    onClose={() => setOpenSelector(null)}
                  />
                )}
              </div>
            ) : (
              <p>-</p>
            )}
          </div>
        </div>
        <div className="general-information-full-item">
          <div className="general-information-full-item-start">
            <p>Accounting</p>
          </div>
          <VerticalLine />
          <div className="general-information-full-item-middle">
            <p>{formatAccountingDisplay(viewData?.accounting)}</p>
          </div>
          <VerticalLine />
          <div className="general-information-full-item-end">
            <p>—</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralInformation;
